import { useEffect } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { Control, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove } from 'react-hook-form'

import { ManagedPicklistCombobox } from '@/components/pages/Admin/BillManagement/Form/ManagedPicklistCombobox'
import {
  emptyLineItem,
  isLineItemFilled,
  type BillLineItemFormValue,
} from '@/components/pages/Admin/BillManagement/Form/billLineItemsShared'
import {
  excelCellInputClass,
  type BillFormGridVariant,
} from '@/components/pages/Admin/BillManagement/Form/billFormViewShared'
import { useBillLineGridKeyboard } from '@/components/pages/Admin/BillManagement/Form/useBillLineGridKeyboard'
import { Button, FormControl, FormField, FormItem, FormMessage, Input } from '@/components/ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatNumber } from '@/utils/number'
import { cn } from '@/lib/utils'

type BillLineItemsGridProps = {
  control: Control<any>
  fields: FieldArrayWithId<any, 'line_items', 'id'>[]
  append: UseFieldArrayAppend<any, 'line_items'>
  remove: UseFieldArrayRemove
  lineItems: BillLineItemFormValue[] | undefined
  isWholesaleSell: boolean
  syncWholesaleRowWeight: (rowIndex: number) => void
  disabled?: boolean
  variant?: BillFormGridVariant
}

const RETAIL_COL_COUNT = 5
const WHOLESALE_COL_COUNT = 9

export function BillLineItemsGrid({
  control,
  fields,
  append,
  remove,
  lineItems,
  isWholesaleSell,
  syncWholesaleRowWeight,
  disabled,
  variant = 'default',
}: BillLineItemsGridProps) {
  const isExcel = variant === 'excel'
  const editableColCount = isWholesaleSell ? WHOLESALE_COL_COUNT : RETAIL_COL_COUNT

  const { gridCellProps } = useBillLineGridKeyboard({
    rowCount: fields.length,
    editableColCount,
    onAppendRow: () => append(emptyLineItem()),
  })

  const ensureNextRowOnPriceFocus = (rowIndex: number) => {
    if (disabled) return
    const currentRow = lineItems?.[rowIndex]
    if (!isLineItemFilled(currentRow)) return
    const nextRow = rowIndex + 1
    if (nextRow < fields.length) return
    append(emptyLineItem())
  }

  useEffect(() => {
    if (!lineItems?.length) return
    const last = lineItems[lineItems.length - 1]
    if (isLineItemFilled(last)) {
      append(emptyLineItem())
    }
  }, [append, lineItems])

  const inputClass = cn(
    isExcel
      ? cn(excelCellInputClass, 'tabular-nums')
      : 'h-9 border-border/80 bg-background px-2 text-sm tabular-nums',
  )
  const textInputClass = cn(
    isExcel ? excelCellInputClass : 'h-9 border-border/80 bg-background px-2 text-sm',
  )
  const cellClass = cn(isExcel ? 'border border-border/60 p-0' : 'px-2 py-1.5')
  const headClass = cn(
    isExcel
      ? 'h-8 border border-border/60 bg-muted/50 px-1.5 text-xs font-semibold'
      : 'h-10 px-2',
  )

  return (
    <div className={cn(!isExcel && 'rounded-lg border border-border/70')}>
      <div className="overflow-x-auto">
      <Table className={cn('min-w-[56rem] text-sm', isExcel && 'border-collapse')}>
        <TableHeader>
          <TableRow className={cn('hover:bg-muted/40', isExcel ? 'bg-muted/50' : 'bg-muted/40')}>
            <TableHead className={cn(headClass, 'w-10 text-center')}>#</TableHead>
            <TableHead className={cn(headClass, 'min-w-[9rem]')}>ประเภททุเรียน</TableHead>
            <TableHead className={cn(headClass, 'min-w-[8rem]')}>เกรด / เบอร์</TableHead>
            <TableHead className={cn(headClass, 'min-w-[9rem]')}>รายละเอียด</TableHead>
            {isWholesaleSell ? (
              <>
                <TableHead className={cn(headClass, 'min-w-[5.5rem]')}>กล่อง</TableHead>
                <TableHead className={cn(headClass, 'min-w-[6.5rem]')}>กก./กล่อง</TableHead>
                <TableHead className={cn(headClass, 'min-w-[8rem]')}>สายรัด</TableHead>
                <TableHead className={cn(headClass, 'min-w-[5.5rem]')}>จำนวนสาย</TableHead>
              </>
            ) : null}
            <TableHead className={cn(headClass, 'min-w-[6rem]')}>น้ำหนัก (กก.)</TableHead>
            <TableHead className={cn(headClass, 'min-w-[6.5rem]')}>ราคา/กก.</TableHead>
            <TableHead className={cn(headClass, 'min-w-[6rem] text-right')}>รวม</TableHead>
            <TableHead className={cn(headClass, 'w-10')} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((fieldRow, index) => {
            const row = lineItems?.[index]
            const rowTotalBaht = (Number(row?.weight_kg) || 0) * (Number(row?.price_per_kg) || 0)
            const weightCol = isWholesaleSell ? 7 : 3
            const priceCol = isWholesaleSell ? 8 : 4

            return (
              <TableRow
                key={fieldRow.id}
                className={cn('align-top', !isExcel && 'hover:bg-muted/20')}
              >
                <TableCell
                  className={cn(
                    cellClass,
                    'text-center text-xs text-muted-foreground',
                    isExcel && 'bg-muted/30 px-1.5 py-1',
                  )}
                >
                  {index + 1}
                </TableCell>
                <TableCell className={cellClass}>
                  <FormField
                    control={control}
                    name={`line_items.${index}.durian_type`}
                    render={({ field }) => (
                      <FormItem className="space-y-0">
                        <FormControl>
                          <ManagedPicklistCombobox
                            kind="durian_type"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder="พิมพ์หรือเลือก"
                            disabled={disabled}
                            className={isExcel ? excelCellInputClass : undefined}
                            gridRow={index}
                            gridCol={0}
                            onGridKeyDown={gridCellProps(index, 0).onKeyDown}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </TableCell>
                <TableCell className={cellClass}>
                  <FormField
                    control={control}
                    name={`line_items.${index}.grade`}
                    render={({ field }) => (
                      <FormItem className="space-y-0">
                        <FormControl>
                          <ManagedPicklistCombobox
                            kind="grade"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder="พิมพ์หรือเลือก"
                            disabled={disabled}
                            className={isExcel ? excelCellInputClass : undefined}
                            gridRow={index}
                            gridCol={1}
                            onGridKeyDown={gridCellProps(index, 1).onKeyDown}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </TableCell>
                <TableCell className={cellClass}>
                  <FormField
                    control={control}
                    name={`line_items.${index}.description`}
                    render={({ field }) => (
                      <FormItem className="space-y-0">
                        <FormControl>
                          <Input
                            className={textInputClass}
                            placeholder="หมายเหตุ"
                            disabled={disabled}
                            {...field}
                            {...gridCellProps(index, 2)}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </TableCell>
                {isWholesaleSell ? (
                  <>
                    <TableCell className={cellClass}>
                      <FormField
                        control={control}
                        name={`line_items.${index}.box_count`}
                        render={({ field }) => (
                          <FormItem className="space-y-0">
                            <FormControl>
                              <Input
                                type="number"
                                step="1"
                                min={1}
                                className={inputClass}
                                disabled={disabled}
                                {...field}
                                value={field.value === 0 ? '' : field.value}
                                onChange={(e) => {
                                  const v = e.target.value
                                  field.onChange(v === '' ? 0 : parseInt(v, 10))
                                  syncWholesaleRowWeight(index)
                                }}
                                {...gridCellProps(index, 3)}
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    <TableCell className={cellClass}>
                      <FormField
                        control={control}
                        name={`line_items.${index}.weight_per_box_kg`}
                        render={({ field }) => (
                          <FormItem className="space-y-0">
                            <FormControl>
                              <Input
                                type="number"
                                step="0.001"
                                min={0}
                                className={inputClass}
                                disabled={disabled}
                                {...field}
                                value={field.value === 0 ? '' : field.value}
                                onChange={(e) => {
                                  const v = e.target.value
                                  field.onChange(v === '' ? 0 : parseFloat(v))
                                  syncWholesaleRowWeight(index)
                                }}
                                {...gridCellProps(index, 4)}
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    <TableCell className={cellClass}>
                      <FormField
                        control={control}
                        name={`line_items.${index}.strap_band`}
                        render={({ field }) => (
                          <FormItem className="space-y-0">
                            <FormControl>
                              <ManagedPicklistCombobox
                                kind="strap_band"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                placeholder="สายรัด"
                                disabled={disabled}
                                className={isExcel ? excelCellInputClass : undefined}
                                gridRow={index}
                                gridCol={5}
                                onGridKeyDown={gridCellProps(index, 5).onKeyDown}
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    <TableCell className={cellClass}>
                      <FormField
                        control={control}
                        name={`line_items.${index}.strap_quantity`}
                        render={({ field }) => (
                          <FormItem className="space-y-0">
                            <FormControl>
                              <Input
                                type="number"
                                step="1"
                                min={1}
                                className={inputClass}
                                disabled={disabled}
                                {...field}
                                value={field.value === 0 ? '' : field.value}
                                onChange={(e) => {
                                  const v = e.target.value
                                  field.onChange(v === '' ? 0 : parseInt(v, 10))
                                }}
                                {...gridCellProps(index, 6)}
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </TableCell>
                  </>
                ) : null}
                <TableCell className={cellClass}>
                  <FormField
                    control={control}
                    name={`line_items.${index}.weight_kg`}
                    render={({ field }) => (
                      <FormItem className="space-y-0">
                        <FormControl>
                          <Input
                            type="number"
                            step="0.001"
                            min={0}
                            readOnly={isWholesaleSell}
                            tabIndex={isWholesaleSell ? -1 : undefined}
                            className={cn(
                              inputClass,
                              isWholesaleSell && 'cursor-not-allowed bg-muted/50',
                            )}
                            disabled={disabled}
                            {...field}
                            value={field.value === 0 ? '' : field.value}
                            onChange={(e) => {
                              const v = e.target.value
                              field.onChange(v === '' ? 0 : parseFloat(v))
                            }}
                            {...gridCellProps(index, weightCol)}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </TableCell>
                <TableCell className={cellClass}>
                  <FormField
                    control={control}
                    name={`line_items.${index}.price_per_kg`}
                    render={({ field }) => (
                      <FormItem className="space-y-0">
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            className={inputClass}
                            disabled={disabled}
                            {...field}
                            value={field.value === 0 ? '' : field.value}
                            onChange={(e) => {
                              const v = e.target.value
                              field.onChange(v === '' ? 0 : parseFloat(v))
                            }}
                            onFocus={() => {
                              ensureNextRowOnPriceFocus(index)
                            }}
                            {...gridCellProps(index, priceCol)}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </TableCell>
                <TableCell
                  className={cn(
                    cellClass,
                    'text-right text-sm font-medium tabular-nums',
                    isExcel && 'px-1.5 py-1',
                  )}
                >
                  {formatNumber(rowTotalBaht)}
                </TableCell>
                <TableCell className={cn(cellClass, isExcel && 'px-0.5 py-0.5')}>
                  {fields.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => remove(index)}
                      aria-label="ลบรายการ"
                      tabIndex={-1}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      </div>
      <div
        className={cn(
          'flex flex-col gap-2 border-t px-3 py-2 sm:flex-row sm:items-center sm:justify-between',
          isExcel ? 'border-border/60 bg-muted/10' : 'border-border/70',
        )}
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 self-start text-xs font-medium"
          disabled={disabled}
          onClick={() => append(emptyLineItem())}
        >
          <Plus className="size-3.5 shrink-0" aria-hidden />
          เพิ่มรายการสินค้า
        </Button>
        {!isExcel ? (
          <p className="text-xs text-muted-foreground">
            ใช้ Tab เลื่อนเซลล์ · Enter ลงแถวถัดไป · แถวใหม่จะถูกเพิ่มอัตโนมัติเมื่อกรอกแถวสุดท้าย
          </p>
        ) : null}
      </div>
    </div>
  )
}
